import { useEffect, useRef, useState } from 'react';
import styles from './GoogleTranslate.module.css';

const GoogleTranslate = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState('EN');
    const [isLoaded, setIsLoaded] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧', short: 'EN' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦', short: 'AR' },
        { code: 'zh-CN', name: '中文', flag: '🇨🇳', short: 'ZH' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺', short: 'RU' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪', short: 'DE' }
    ];

    useEffect(() => {
        // Check current language from cookie
        const getCookie = (name: string) => {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? match[2] : null;
        };

        const googtrans = getCookie('googtrans');
        if (googtrans) {
            const lang = googtrans.split('/').pop();
            const found = languages.find(l => l.code === lang);
            if (found) setCurrentLang(found.short);
        }

        // Initialize Google Translate
        (window as any).googleTranslateElementInit = () => {
            if ((window as any).google && (window as any).google.translate) {
                new (window as any).google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                        includedLanguages: 'ar,zh-CN,ru,de',
                        autoDisplay: false
                    },
                    'google_translate_element'
                );
                setIsLoaded(true);
            }
        };

        // Load Google Translate script
        const existingScript = document.getElementById('google-translate-script');
        if (!existingScript) {
            const script = document.createElement('script');
            script.id = 'google-translate-script';
            script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.async = true;
            document.body.appendChild(script);
        } else if ((window as any).google && (window as any).google.translate && !isLoaded) {
            (window as any).googleTranslateElementInit();
        }

        // Hide the banner aggressively
        const hideBanner = () => {
            // Hide iframe banner
            const frames = document.querySelectorAll('.goog-te-banner-frame, iframe.goog-te-banner-frame');
            frames.forEach(frame => {
                (frame as HTMLElement).style.display = 'none';
                (frame as HTMLElement).style.visibility = 'hidden';
                (frame as HTMLElement).style.height = '0';
            });

            // Reset body position
            document.body.style.top = '0px';
            document.body.style.position = '';

            // Hide tooltip
            const tooltip = document.getElementById('goog-gt-tt');
            if (tooltip) tooltip.style.display = 'none';

            // Hide balloons
            const balloons = document.querySelectorAll('.goog-te-balloon-frame');
            balloons.forEach(b => (b as HTMLElement).style.display = 'none');
        };

        const interval = setInterval(hideBanner, 100);
        hideBanner();

        // Close dropdown on outside click
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);

        return () => {
            clearInterval(interval);
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const selectLanguage = (langCode: string, shortName: string) => {
        setCurrentLang(shortName);
        setIsOpen(false);

        if (langCode === 'en') {
            // Reset to English - clear cookies and reload
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + window.location.hostname;
            window.location.reload();
            return;
        }

        // Try using the combo box first
        const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (select) {
            select.value = langCode;
            select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            // Fallback: Set cookie and reload
            const domain = window.location.hostname;
            document.cookie = `googtrans=/en/${langCode}; path=/`;
            document.cookie = `googtrans=/en/${langCode}; path=/; domain=${domain}`;
            document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${domain}`;
            window.location.reload();
        }
    };

    return (
        <>
            {/* Google Translate element - hidden but functional */}
            <div
                id="google_translate_element"
                style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none',
                    height: 0,
                    overflow: 'hidden'
                }}
            ></div>

            {/* Custom Language Selector */}
            <div className={styles.customLangSelector} ref={dropdownRef}>
                <button
                    className={styles.langToggle}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Select language"
                >
                    <i className="fa-solid fa-globe"></i>
                    <span className={styles.currentLang}>{currentLang}</span>
                    <i className={`fa-solid fa-chevron-down ${styles.langArrow} ${isOpen ? styles.open : ''}`}></i>
                </button>

                {isOpen && (
                    <div className={styles.langDropdown}>
                        {languages.map(lang => (
                            <button
                                key={lang.code}
                                className={`${styles.langOption} ${currentLang === lang.short ? styles.active : ''}`}
                                onClick={() => selectLanguage(lang.code, lang.short)}
                            >
                                <span className={styles.langFlag}>{lang.flag}</span>
                                <span className={styles.langName}>{lang.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default GoogleTranslate;
